import React, { useEffect, useState } from "react";
import { Pencil, ImagePlus, Save, Loader2 } from "lucide-react";
import { useEditMode } from "@/contexts/CmsContext";
import {
  commitStagedTexts,
  discardStagedTexts,
  subscribeStagedCount,
} from "@/lib/slotCache";

/**
 * Floating CMS edit controls.
 * - Fixed bottom-left, directly above the FloatingLanguageSwitcher.
 * - In TEXT edit mode an extra Save button appears next to the Exit
 *   toggle. Edits made inline are staged locally until Save is clicked;
 *   only then does the backend translate the new content into the
 *   other languages. Exit deactivates Edit Mode and discards any
 *   unsaved staged edits (after a confirm prompt if any exist).
 */
const FloatingCmsButtons = () => {
  const { imageEditMode, textEditMode, toggleImage, toggleText } = useEditMode();
  const [stagedCount, setStagedCount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => subscribeStagedCount(setStagedCount), []);

  // When the user leaves Edit Mode any staged-but-unsaved edits must
  // be cleared so the page reverts to the last server snapshot.
  useEffect(() => {
    if (!textEditMode) discardStagedTexts();
  }, [textEditMode]);

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const res = await commitStagedTexts();
      if (res?.saved) {
        setJustSaved(true);
        window.setTimeout(() => setJustSaved(false), 1800);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleExitText = () => {
    if (stagedCount > 0) {
      const ok = window.confirm(
        `Discard ${stagedCount} unsaved text change${stagedCount === 1 ? "" : "s"} and exit Edit Mode?`
      );
      if (!ok) return;
    }
    toggleText();
  };

  const btnClass = (active) =>
    `inline-flex items-center justify-center w-11 h-11 backdrop-blur-md border shadow-[0_4px_24px_rgba(28,22,19,0.08)] transition-colors ${
      active
        ? "bg-[#C16542] text-[#FDFBF7] border-[#C16542]"
        : "bg-[#FDFBF7]/90 text-[#2C2621] border-[#2C2621]/15 hover:border-[#C16542]/60 hover:text-[#C16542]"
    }`;

  return (
    <div
      className="fixed bottom-[72px] left-5 md:bottom-[96px] md:left-7 z-40 flex flex-col gap-1.5"
      data-edit-allow="true"
      data-no-cms-edit="true"
      data-cms-ui="true"
      data-testid="floating-cms-buttons"
    >
      {textEditMode && (
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || stagedCount === 0}
          className={`relative inline-flex items-center justify-center w-11 h-11 backdrop-blur-md border shadow-[0_4px_24px_rgba(28,22,19,0.08)] transition-colors ${
            stagedCount === 0
              ? "bg-[#FDFBF7]/70 text-[#2C2621]/40 border-[#2C2621]/10 cursor-not-allowed"
              : "bg-[#2C2621] text-[#FDFBF7] border-[#2C2621] hover:bg-[#C16542] hover:border-[#C16542]"
          }`}
          aria-label={
            saving
              ? "Saving changes"
              : stagedCount === 0
              ? "No unsaved changes"
              : `Save ${stagedCount} unsaved change${stagedCount === 1 ? "" : "s"} and translate`
          }
          title={
            stagedCount === 0
              ? "No unsaved changes"
              : `Save ${stagedCount} change${stagedCount === 1 ? "" : "s"} & translate to FR / ES / EN`
          }
          data-edit-allow="true"
          data-testid="floating-cms-save-text"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.7} />
          ) : (
            <Save className="w-4 h-4" strokeWidth={1.7} />
          )}
          {stagedCount > 0 && !saving && (
            <span
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#C16542] text-[#FDFBF7] text-[10px] font-medium leading-[18px] text-center border border-[#FDFBF7]"
              data-testid="floating-cms-staged-count"
            >
              {stagedCount}
            </span>
          )}
          {justSaved && (
            <span
              className="absolute right-12 whitespace-nowrap text-[10px] tracking-[0.22em] uppercase text-[#C16542] bg-[#FDFBF7]/95 border border-[#C16542]/40 px-2.5 py-1.5 shadow-sm"
              data-testid="floating-cms-saved-toast"
            >
              Saved · translating…
            </span>
          )}
        </button>
      )}
      <button
        type="button"
        onClick={toggleImage}
        className={btnClass(imageEditMode)}
        aria-pressed={imageEditMode}
        aria-label={imageEditMode ? "Exit image edit mode" : "Edit images"}
        title={imageEditMode ? "Exit image edit mode" : "Edit images"}
        data-edit-allow="true"
        data-testid="floating-cms-toggle-image"
      >
        <ImagePlus className="w-4 h-4" strokeWidth={1.6} />
      </button>
      <button
        type="button"
        onClick={textEditMode ? handleExitText : toggleText}
        className={btnClass(textEditMode)}
        aria-pressed={textEditMode}
        aria-label={textEditMode ? "Exit text edit mode" : "Edit text"}
        title={textEditMode ? "Exit text edit mode" : "Edit text"}
        data-edit-allow="true"
        data-testid="floating-cms-toggle-text"
      >
        <Pencil className="w-4 h-4" strokeWidth={1.6} />
      </button>
    </div>
  );
};

export default FloatingCmsButtons;
